import { Badge, Box, Button, Flex } from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { SettingsIcon } from "@chakra-ui/icons";
import { NavLink, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Ticket, TicketPriority, TicketStatus } from "../types/ticket.types";
import { useCenterTickets, useTickets } from "../hooks/tickets.hooks";
import UserAvatar from "../components/UserAvatar";
import { format } from "date-fns";
import { useTicketLabel, useTicketLabels } from "../hooks/ticket_labels.hooks";
import DataTable, { SearchBar } from "../components/table/ServerSideTable";
import TicketStatusBadge from "../components/TicketStatusBadge";
import TicketPriorityBadge from "../components/TicketPriorityBadge";
import Select from "../components/inputs/Select";
import { useConnectedUser } from "../hooks/user.hooks";
import { useCenters } from "../hooks/centers.hooks";
import { useDataTableContext } from "../hooks/data-table.hooks";
import { getLongExposedId } from "../services/command.service";

const TicketLabelBadge = ({ id }: { id: number | string }) => {
  const ticketLabel = useTicketLabel(id);

  if (!ticketLabel) {
    return <>---</>;
  }
  return (
    <Badge
      h="1.5rem"
      lineHeight="1.5rem"
      px="1rem"
      rounded="md"
      backgroundColor={"gray.50"}
      color={"gray.700"}
      border={"solid 1px"}
      borderColor={"gray.300"}
      textTransform={"capitalize"}
      w="fit-content"
    >
      {ticketLabel.label}
    </Badge>
  );
};

const TicketingTableActionComponent = () => {
  const { filters, setFilters } = useDataTableContext();
  const { data: reasons } = useTicketLabels({});

  return (
    <Flex gap={4}>
      {reasons && (
        <Select
          placeholder="Motif"
          value={filters && filters.reason}
          onChange={(evt) =>
            setFilters && setFilters("reason", evt.target.value)
          }
        >
          {reasons.map((reason) => (
            <option key={reason.id} value={reason.id}>
              {reason.label}
            </option>
          ))}
        </Select>
      )}
      <Select
        placeholder="Statut"
        value={filters && filters.status}
        onChange={(evt) => setFilters && setFilters("status", evt.target.value)}
      >
        <option value={TicketStatus.NEW}>Nouveau</option>
        <option value={TicketStatus.BLOCKED}>En attente d'éléments</option>
        <option value={TicketStatus.CLOSED}>Fermé</option>
        <option value={TicketStatus.OPEN}>En cours</option>
      </Select>
      <Select
        placeholder="Priorité"
        value={filters && filters.priority}
        onChange={(evt) =>
          setFilters && setFilters("priority", evt.target.value)
        }
      >
        <option value={TicketPriority.LOW}>Normale</option>
        <option value={TicketPriority.MEDIUM}>Moyenne</option>
        <option value={TicketPriority.HIGH}>Haute</option>
      </Select>
    </Flex>
  );
};

function TicketingPage() {
  const navigate = useNavigate();
  const user = useConnectedUser();
  const { center } = useCenters();
  const columns = useMemo<ColumnDef<Ticket>[]>(
    () => [
      {
        accessorFn: (row) => `${format(row.createdAt, "yyyy")}-${row.id}`,
        id: "id",
        header: "ID",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <TicketLabelBadge id={row.ticketLabelId}></TicketLabelBadge>
        ),
        id: "ticket_label_id",
        header: "Motif",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <TicketStatusBadge
            id={row.id}
            editable={false}
          ></TicketStatusBadge>
        ),
        id: "status",
        header: "Statut",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => `${format(row.createdAt, "dd/MM/yyyy")}`,
        id: "createdAt",
        header: "Date de création",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => {
          if (!row.openById) {
            return <></>;
          }
          return <UserAvatar userId={row.openById}></UserAvatar>;
        },
        id: "owner_id",
        header: "Ouvert par",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => {
          if (row.commandId) {
            return (
              <>
                <NavLink to={`/orders/${row.commandId}`}>
                  <Box
                    textOverflow={"ellipsis"}
                    whiteSpace={"nowrap"}
                    overflow={"hidden"}
                    color="brand.700"
                    textDecoration="underline"
                  >
                    {row.command ? getLongExposedId(row.command) : "---"}
                  </Box>
                </NavLink>
              </>
            );
          }
          return <>---</>;
        },
        id: "command_id",
        header: "Lié à",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <TicketPriorityBadge priority={row.priority}></TicketPriorityBadge>
        ),
        id: "priority",
        header: "Priorité",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
    ],
    []
  );

  if (user?.isSuperAdmin === false && center === null) {
    return;
  }
  return (
    <>
      <Flex justifyContent={"space-between"}>
        <PageTitle>Tickets SAV / Assistance</PageTitle>
        <Flex gap="4">
          {user?.isSuperAdmin && (
            <Button w="fit-content" onClick={() => navigate("/tickets/labels")}>
              <SettingsIcon mr={4}></SettingsIcon>Gérer les libellés
            </Button>
          )}
        </Flex>
      </Flex>

      <DataTable
        columns={columns}
        paginatedQuery={
          user?.isSuperAdmin
            ? useTickets
            : // eslint-disable-next-line react-hooks/rules-of-hooks
              useCenterTickets({ id: center ? center.id : Number.NaN })
        }
        defaultSortBy={[
          {
            id: "created_at",
            desc: true,
          },
        ]}
        w={[
          "100px",
          "300px",
          "200px",
          "120px",
          "300px",
          "100px",
          "200px",
          "2rem",
        ]}
        flexGrow={[1, 1, 1, 1, 1, 2, 1]}
        toolbar={
          <Flex my="1rem" justifyContent="space-between">
            <SearchBar />
            <TicketingTableActionComponent />
          </Flex>
        }
        cellLink={(cell) => {
          if (cell.id.endsWith("linkedTo")) {
            return;
          }
          return `/tickets/${cell.row.id}`;
        }}
      ></DataTable>
    </>
  );
}

export default TicketingPage;
